/**
 * The organization's access log — Settings → «Журнал доступа» (#2492).
 * Spec: docs/plans/2026-11-03-settings-access-log.md.
 *
 * Reads `access_audit_events`: every change that moved someone's access in the
 * org (E1.5, #1336). `ACCESS_AUDIT_ACTIONS` is the CLOSED list of what the
 * writer (`apps/edge-api/src/utils/access-change.ts`) records — the writer's
 * type is this enum, so a new action cannot be written without the log
 * learning to show it.
 *
 * The answer is a narrow parsed shape, not the stored `details_json`: who, about
 * whom, the role before/after and the grant preset. A person is named only
 * while they are a member of the organization — exactly what the same reader
 * sees in the team list (`GET /users`, the same `user.list` rule).
 */
import { z } from 'zod/v4';
import { accessPresetSchema } from './access';
import { userRoleSchema } from './users';

export const accessAuditActionSchema = z.enum([
  'member.add',
  'member.remove',
  'role.change',
  'projects.change',
  'org_structure.change',
  'external_id.change',
  'user.merge',
  // A narrow analytics grant assigned (or replaced) and revoked — from
  // «Settings → Team» (`routes/users/access.ts`) or by a system write.
  'access.grant',
  'access.revoke',
]);
export const ACCESS_AUDIT_ACTIONS = accessAuditActionSchema.options;
export type AccessAuditAction = z.infer<typeof accessAuditActionSchema>;

/**
 * The screen's filter: real kinds of events, not the prototype's
 * «User / System / Alert» (no such events exist).
 */
export const accessLogCategorySchema = z.enum([
  'membership',
  'grants',
  'projects',
  'structure',
]);
export type AccessLogCategory = z.infer<typeof accessLogCategorySchema>;

export const ACCESS_LOG_CATEGORY_ACTIONS: Readonly<
  Record<AccessLogCategory, readonly AccessAuditAction[]>
> = {
  membership: [
    'member.add',
    'member.remove',
    'role.change',
    'user.merge',
    'external_id.change',
  ],
  // And a `member.remove` that revoked a grant (`grantRevoked`): the
  // repository adds those to this category — access went with the membership.
  grants: ['access.grant', 'access.revoke'],
  projects: ['projects.change'],
  structure: ['org_structure.change'],
};

export const ACCESS_LOG_PAGE_MAX = 100;
export const ACCESS_LOG_PAGE_DEFAULT = 50;

/**
 * Keyset cursor `<created_at>|<id>`: the log grows at the top, so an offset
 * would shift every page under the reader. `created_at` is always the column
 * default (ISO, `record` never sets it) and ids are UUIDs.
 */
const ACCESS_LOG_CURSOR_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z\|[A-Za-z0-9-]{1,64}$/;

export const accessLogQuerySchema = z.object({
  category: accessLogCategorySchema.optional(),
  cursor: z.string().regex(ACCESS_LOG_CURSOR_PATTERN).optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(ACCESS_LOG_PAGE_MAX)
    .default(ACCESS_LOG_PAGE_DEFAULT),
});

/**
 * Who acted or whom it was about.
 * - `member` — a current member of the org: the team list shows the same name
 *   and e-mail to the same reader;
 * - `platform` — the Aelo platform owner, not a member of this org;
 * - `former` — no longer a member (or the account was deleted / merged away):
 *   no name, the reader no longer sees this person anywhere else;
 * - `system` — an actor only: a change no person made (a grant revoked or
 *   narrowed because its role, department or project went away), which the
 *   writer records under the holder's own id (`recordPrunedGrants`,
 *   `systemRevokedGrantChange`).
 */
export const accessLogPersonSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('member'),
    userId: z.string(),
    /** May be empty — the screen then shows the e-mail. */
    name: z.string(),
    email: z.string(),
  }),
  z.object({ kind: z.literal('platform') }),
  z.object({ kind: z.literal('former') }),
  z.object({ kind: z.literal('system') }),
]);

/** How many affected people one event names at most; `affectedCount` has the rest. */
export const ACCESS_LOG_AFFECTED_MAX = 10;
export type AccessLogPerson = z.infer<typeof accessLogPersonSchema>;

export const accessLogEventSchema = z.object({
  id: z.string(),
  action: accessAuditActionSchema,
  createdAt: z.string(),
  actor: accessLogPersonSchema,
  /** Null when the change is not about one person (an org-structure import). */
  target: accessLogPersonSchema.nullable(),
  /** Role before the change: `role.change`, `member.remove`. */
  roleBefore: userRoleSchema.nullable(),
  /** Role after: `role.change`, and the role given by `member.add`. */
  roleAfter: userRoleSchema.nullable(),
  /** The grant's preset: `access.grant`, `access.revoke`. */
  preset: accessPresetSchema.nullable(),
  /**
   * `access.grant` that replaced or narrowed an earlier grant (its stored
   * `before`) — «changed», not «granted».
   */
  replacesGrant: z.boolean(),
  /** `member.remove` that also revoked the member's analytics grant. */
  grantRevoked: z.boolean(),
  /**
   * The people a change of SEVERAL moved, when it has no one `target`: a
   * project's supervisor roster, a CRM user sync, an org-structure import or
   * a department head. At most `ACCESS_LOG_AFFECTED_MAX`, by the same naming
   * rule as `actor`.
   */
  affected: z.array(accessLogPersonSchema).max(ACCESS_LOG_AFFECTED_MAX),
  /**
   * Everyone the change moved, including those beyond `affected` and CRM
   * employees without an account yet (an import's pending members — counted,
   * never named: the log has no name for them).
   */
  affectedCount: z.number().int().nonnegative(),
});
export type AccessLogEvent = z.infer<typeof accessLogEventSchema>;

export const accessLogResponseSchema = z.object({
  events: z.array(accessLogEventSchema),
  /** Null on the last page. */
  nextCursor: z.string().nullable(),
});
export type AccessLogResponse = z.infer<typeof accessLogResponseSchema>;
